import React from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { SchoolyearsMenuItems } from './SchoolyearsMenuItems';
import { SchoolsMenuItems } from './SchoolsMenuItems';
import 'twin.macro';
import { Breadcrumb } from '@teetsh/app/src/components/Breadcrumb';
import Link from '@teetsh/app/src/components/Link';
import { SCHOOLS_KEY } from '@teetsh/app/src/queries/schools/useSchools';
import { useNavigate, useLocation } from 'react-router-dom';
import { queryClient } from 'queryClient';
import { setSchoolyearAndSchoolInUrl } from './urlUtils';
import { useTranslation } from 'react-i18next';
import { Menu } from 'components/Menu';
import { routes } from 'containers/routes';
import {
  AdjustmentsVerticalIcon as Adjustments,
  PlusIcon,
} from '@heroicons/react/24/outline';
import Tooltip2 from 'components/Tooltip2';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useSetSkipBookmarkWarning } from 'containers/BookmarkToPastYearMessage';
import useSchoolyearsWithSchoolsAndClassrooms from 'queries/schoolyears/useSchoolyearsWithSchoolsAndClassrooms';

export function SchoolyearsBreadcrumb() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { data: schoolyears = [] } = useSchoolyearsWithSchoolsAndClassrooms();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'schoolyearSettings']);
  const setSkipBookmarkWarning = useSetSkipBookmarkWarning();

  if (schoolyear === undefined) {
    return <div />;
  }
  const firstError = schoolyear.errors?.[0];

  return (
    <Breadcrumb
      dividerIcon="dot"
      items={[
        {
          id: schoolyear.id,
          text: schoolyear.label,
          buttonAriaLabel: t('common:breadcrumbs.changeSchoolYear'),
          iconElement: firstError ? (
            <Tooltip2
              label={t('schoolyearSettings:periods.errorTooltip')}
              tw="mr-2"
            >
              <Link to={`/mes-annees-scolaires/${schoolyear.id}/holidays`}>
                <ExclamationTriangleIcon tw="h-5 w-5 -mb-0.5 p-0.5 rounded-full text-warning-moderate hover:bg-warning-moderate" />
              </Link>
            </Tooltip2>
          ) : undefined,
          subItemComponent: () => (
            <SchoolyearsMenuItems
              onClick={scy => {
                const schools =
                  schoolyears.find(s => s.id === scy.id)?.schools || [];
                const nextSchool =
                  schools.find(
                    s =>
                      s.name.toLocaleLowerCase() ===
                      school.name.toLocaleLowerCase(),
                  ) || schools[0]; // try to keep the school that has the same name if possible, otherwise navigate to the first school

                const url = setSchoolyearAndSchoolInUrl({
                  url: location.pathname,
                  prevSchoolYear: schoolyear,
                  nextSchoolYear: scy,
                  nextSchool: nextSchool,
                });

                setSkipBookmarkWarning(true); // do not show warning if the user navigates from one year to another
                queryClient.invalidateQueries({ queryKey: [SCHOOLS_KEY] });
                navigate(url);
              }}
              schoolyear={schoolyear}
              label={t('common:selectSchoolyear')}
            >
              <Menu.Item
                text={t('common:menu.schoolyearSettings')}
                to={routes.schoolyearsSettings}
                icon={Adjustments}
              />
              <Menu.Item
                text={t('schoolyearSettings:createSchoolYear')}
                to={`${routes.schoolyearsSettings}/new`}
                icon={PlusIcon}
              />
            </SchoolyearsMenuItems>
          ),
        },
        {
          id: school.id,
          text: school.name,
          buttonAriaLabel: t('common:breadcrumbs.changeSchool'),
          subItemComponent: () => (
            <SchoolsMenuItems
              schoolyearId={schoolyear.id}
              school={school}
              label={t('common:selectSchool')}
            >
              <Menu.Item
                text={t('common:menu.schoolSettings')}
                to={`${routes.schoolyearsSettings}/${schoolyear.id}/ecoles/${school.id}`}
                icon={Adjustments}
              />
              <Menu.Item
                text={t('schoolyearSettings:addSchool')}
                to={`${routes.schoolyearsSettings}/${schoolyear.id}/ecoles/new`}
                icon={PlusIcon}
              />
            </SchoolsMenuItems>
          ),
        },
      ]}
    />
  );
}
